import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, TextField, Button, Typography, Alert } from '@mui/material';
import logo from '../../styles/quality-shield-logo.png'; 
import axiosInstance from './api';

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [successMessage, setSuccessMessage] = useState(null); // State for success alert
  const [codeError, setCodeError] = useState(""); 

  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [codePage, setCodePage] = useState(false); // State to track if 2FA code page is needed
  const [two_factor_code, setTwoFactorCode] = useState(''); // State for 2FA code input
  const history = useHistory();
  const [showResetPassword, setShowResetPassword] = useState(false); // State to toggle between login and reset password


  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await fetch('/api/login/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.toLowerCase(), password }),
      });

      if (!response.ok) {
        throw new Error('Invalid credentials');
      }

      const data = await response.json();
      if (data.message.includes('2FA')) {
        setCodePage(true);
      }
      

    } catch (err) {
      setError(err.message);
    }
  };
  
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!two_factor_code || two_factor_code.trim() === "") {
      setCodeError("2FA code cannot be empty");
      return;
    }
    setCodeError("");

    try {
      const response = await fetch('/api/login/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: two_factor_code, email: email.toLowerCase(), password: password }), // Reusing password state for 2FA code
      });

      if (!response.ok) {
        throw new Error('Invalid 2FA code');
      }

      const data = await response.json();
      localStorage.setItem('token', data.token); // Save token to localStorage
      axiosInstance.defaults.headers['Authorization'] = 'Token ' + data.token;

      // Fetch user data after successful login
      const userResponse = await axiosInstance('/api/login/');
      const userData = userResponse.data;
      localStorage.setItem('user', JSON.stringify(userData)); // Save user data to localStorage

      onLogin(); // Notify the app that the user has logged in
      history.push('/'); // Redirect to the dashboard or home page
    } catch (err) {
      setError(err.message);
    }
  }

  function handleCodeChange(e) {
    setTwoFactorCode(e.target.value);
  }

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      axiosInstance.post('/api/password-reset-request/', {
        email: resetEmail.toLowerCase(),
      }).then(response => {
        setSuccessMessage('Password reset email sent successfully! Please check your inbox.');
        
        setShowResetPassword(false); // Hide reset password form after submission
      }).catch(error => {
        setError('Failed to send password reset email. Please try again.');
      });
    } catch (err) {
      setError(err.message);
    }
  }

  const triggerPasswordReset = () => {
    setShowResetPassword(true);
  }
  const handleResetEmailChange = (e) => {
    setResetEmail(e.target.value);
  };

  return (
    <Box
      sx={{
        maxWidth: 400,
        mx: 'auto',
        mt: 8,
        p: 4,
        borderRadius: 2,
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        backgroundColor: '#ffffff',
        textAlign: 'center',
      }}
    >
      {/* Logo */}
      <Box sx={{ mb: 3 }}>
        <img src={logo} alt="QualityShield Logo" style={{ width: '150px' }} />
      </Box>

      {/* Title */}
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#333' }}>
        Login
      </Typography>

      {/* Success Message */}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      {/* Error Message */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {/* Login Form */}
      
      {
        showResetPassword ? (
          <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please enter your email associated with your account
          </Typography>
          <form onSubmit={handleResetSubmit}>
          <TextField
            label="Email"
            type="text"
            fullWidth
            margin="normal"
            value={resetEmail} // Reusing email state for 2FA code input
            onChange={handleResetEmailChange}
          />
          <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
            Send reset email
          </Button>
        </form>
        </>
        ) :
        codePage ? ( 
          <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please enter your 2FA code sent to your email.
          </Typography>
          <form onSubmit={handleCodeSubmit}>
            <TextField
              label="2FA Code"
              type="text"
              fullWidth
              margin="normal"
              value={two_factor_code} // Reusing email state for 2FA code input
              onChange={handleCodeChange}
              error={!!codeError}
              helperText={codeError}
            />
            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
              Verify Code
            </Button>
          </form>
          </>
        ) : (
          <form onSubmit={handleLogin}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
              Login
            </Button>
            <Button color="primary" sx={{ mt: 2, ml: 1 }} onClick={triggerPasswordReset}>
              Forgot Password?
            </Button>
          </form>
          
        )
      }
    </Box>
  );
};

export default Login;
